import { inject, Injectable, Provider } from "@angular/core";
import { BasicError } from "projects/shared/lib/error/basic-error";
import { map, Observable, of, Subscription, switchMap } from "rxjs";
import { ENVIRONMENT } from "projects/shared/lib/environment/environment";
import { IProductApiService, IProductApiServiceToken } from "../../api/product/product-api-service.interface";
import { IAuthService, IAuthServiceToken } from "../../infrastructure/authentication/auth-service.interface";
import {
  AddressDto, EError,
  EImageSize, EProductAssetType, IProductAvailabilityGetQuery, IProductAvailabilityGetQueryResult, ProductAssetDto, ProductAssetGetByIdQueryResult,
  ProductAssetGetQueryResult, ProductAssetItemDto, ProductAssortmentGetQueryResult,
  ProductAvailabilityDto, ProductDetailGetQueryResult,
  ProductGetByIntegraQueryQueryResult, ProductGetByProductCategoryQueryResult, ProductGetByTechGroupFilterQueryResult,
  ProductGetCategoryTreeQueryResult, ProductIdentifierDto, ProductImageDto,
  ProductOptionalGetQueryResult, ProductOrderPriceGetItemDto, ProductOrderPriceGetQueryResult,
  ProductPackagingTypeGetQueryResult, ProductPriceInfoGetItemDto, ProductPriceInfoGetQueryResult,
  ProductPromotionDetailGetQueryResult,
  ProductPromotionGetQueryResult, ProductQuantityScaleGetQueryResult, ProductSearchQueryResult,
  ProductStockInformationGetDto, ProductStockInformationGetQueryResult, ProductThumbnailGetQueryResult, TechGroupFilterDto
} from "../../INTEGRA_Shop_Client";
import { IProductService, IProductServiceToken } from "./product-service.interface";
import { ErrorCodeService } from "../../infrastructure/error-code.service";
import { IComparisonService, IComparisonServiceToken } from "../../infrastructure/comparison/comparison-service.interface";
import { ILoggerService, ILoggerServiceToken } from "../../infrastructure/logger/logger-service.interface";
import { IAssetsService, IAssetsServiceToken } from "shared/services/ui/assets/assets-service.interface";

@Injectable({
  providedIn: 'root'
})
export class ProductService implements IProductService {

  protected readonly productApiService: IProductApiService;
  protected readonly logger: ILoggerService;
  protected readonly authService: IAuthService;
  protected readonly comparisonService: IComparisonService;
  protected readonly assetsService: IAssetsService = inject(IAssetsServiceToken);

  private authenticationSubscription: Subscription | undefined;

  protected shouldLoadPrices: boolean;
  protected allowDocumentDownload: boolean;

  public cartDeliveryDate: Date | undefined;
  public cartDeliveryAddress: AddressDto | undefined;

  public constructor() {
    this.productApiService = inject(IProductApiServiceToken);
    this.logger = inject(ILoggerServiceToken);
    this.authService = inject(IAuthServiceToken);
    this.comparisonService = inject(IComparisonServiceToken);


    let isAuthenticated = this.authService.isAuthenticated();
    this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();
    this.allowDocumentDownload = ENVIRONMENT.allowAnonymousDocumentAccess || isAuthenticated;

    this.authenticationSubscriptionInit();
  }

  protected shouldLoadPricesCurrentStateGet(): boolean {
    return ENVIRONMENT.price.allowAnonymousPrice || this.authService.isAuthenticated();
  }

  private authenticationSubscriptionInit() {
    if (!this.authenticationSubscription) {
      this.authenticationSubscription = this.authService.authenticatedGet().subscribe(
        (isAuthenticated) => {
          this.shouldLoadPrices = this.shouldLoadPricesCurrentStateGet();
          this.allowDocumentDownload = ENVIRONMENT.allowAnonymousDocumentAccess || isAuthenticated;
        }
      );
    }
  }

  public productIdentifierGet(
    productList?: ProductIdentifierDto[] | undefined,
  ): Observable<ProductIdentifierDto> {
    return this.productApiService.productIdentifierGet(productList);
  }

  public productSearch(
    searchtext?: string | undefined,
    maxResultCount?: number | undefined
  ): Observable<ProductSearchQueryResult> {
    return this.productApiService.productSearch(searchtext, maxResultCount);
  }

  public productDetailGet(
    loadDetailText: boolean,
    loadToolTipText: boolean,
    loadProductComparsionText: boolean,
    loadMetaDescriptionText: boolean,
    loadTagList: boolean,
    checkAvailabilityNotification: boolean,
    checkIsPromotionProduct: boolean,
    cartDate?: Date | undefined,
    productList?: ProductIdentifierDto[] | undefined,
    useCancellationLogic: boolean | undefined = true
  ): Observable<ProductDetailGetQueryResult> {
    return this.productApiService.productDetailGet(
      loadDetailText,
      loadToolTipText,
      loadProductComparsionText,
      loadMetaDescriptionText,
      loadTagList,
      checkAvailabilityNotification,
      checkIsPromotionProduct,
      cartDate,
      productList,
      useCancellationLogic
    );
  }

  public productOrderPriceGet(productList?: ProductOrderPriceGetItemDto[] | undefined): Observable<ProductOrderPriceGetQueryResult> {
    if (!this.shouldLoadPrices) {
      this.logger.error("Tried to load prices in service though they aren't allowed.", this.constructor.name);
      throw new BasicError("Illegal access", ErrorCodeService.formatErrorCode(EError.F140210));
    }

    if (productList == undefined || productList.length <= 0) {
      return of(new ProductOrderPriceGetQueryResult());
    }

    return this.productApiService.productOrderPriceGet(productList);
  }

  public productPriceInfoGet(
    productList?: ProductPriceInfoGetItemDto[] | undefined,
    useCancellationLogic: boolean | undefined = true
  ): Observable<ProductPriceInfoGetQueryResult> {
    if (!this.shouldLoadPrices) {
      this.logger.error("Tried to load price informations in service though they aren't allowed.", this.constructor.name);
      throw new BasicError("Illegal access", ErrorCodeService.formatErrorCode(EError.F140220));
    }

    if (productList == undefined || productList.length <= 0) {
      return of(new ProductPriceInfoGetQueryResult());
    }

    return this.productApiService.productPriceInfoGet(productList, undefined, useCancellationLogic);
  }

  public productAvailabilityGet(
    params?: Partial<IProductAvailabilityGetQuery>,
    allowCancellation: boolean | undefined = false
  ): Observable<ProductAvailabilityDto[] | undefined> {
    if (params?.productList == undefined || params.productList.length <= 0) {
      return of([]);
    }

    return this.productApiService.productAvailabilityGet(params, allowCancellation).pipe(
      map((result: IProductAvailabilityGetQueryResult) => result.productList)
    );
  }

  public productStockInformationGet(
    productList?: ProductStockInformationGetDto[] | undefined,
    requestDate?: Date | undefined,
    useCancellationLogic: boolean | undefined = true
  ): Observable<ProductStockInformationGetQueryResult> {
    if (productList == undefined || productList.length <= 0) {
      return of(new ProductStockInformationGetQueryResult());
    }
    return this.productApiService.productStockInformationGet(productList, requestDate, useCancellationLogic);
  }

  public productThumbnailGet(
    imageSize: EImageSize,
    productList?: ProductIdentifierDto[] | undefined,
    useCancellationLogic: boolean | undefined = true
  ): Observable<ProductThumbnailGetQueryResult> {
    let productThumbnailGetQueryResult = new ProductThumbnailGetQueryResult({
      productList: []
    });

    if (productList == undefined || productList.length <= 0) {
      return of(productThumbnailGetQueryResult);
    }

    try {
      return this.productApiService.productThumbnailGet(imageSize, productList, useCancellationLogic).pipe(
        switchMap(
          async (result) => {

            let queryThumbnailList = result.productList?.filter(t => t.productIdentifier != undefined && t.content != undefined);
            let productIdentifierResultMissingList: ProductIdentifierDto[] = [];

            productList.forEach(
              (productIdentifier => {
                let match = queryThumbnailList?.find(
                  (resultEntry: any) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(productIdentifier, resultEntry.productIdentifier)
                );

                if (match == undefined) {
                  productIdentifierResultMissingList.push(productIdentifier);
                }
              })
            );

            let productDefaultImage: string = await this.assetsService.productDefaultImageGetAsync();
            productIdentifierResultMissingList.forEach(
              (productIdentifier) => {
                queryThumbnailList?.push(new ProductImageDto({
                  productIdentifier: productIdentifier,
                  contentType: 'image/jpg',
                  content: productDefaultImage
                }));
              }
            );

            result.productList = queryThumbnailList;

            return result;
          }
        )
      );
    } catch (error) {
      this.logger.error(error, this.constructor.name);
      return of(productThumbnailGetQueryResult);
    }
  }

  public productAssetGet(productList?: ProductIdentifierDto[] | undefined, productAssetType?: EProductAssetType | undefined): Observable<ProductAssetGetQueryResult> {
    let productAssetGetQueryResult = new ProductAssetGetQueryResult();
    productAssetGetQueryResult.productList = [];

    if (productList == undefined || productList.length <= 0) {
      return of(productAssetGetQueryResult);
    }

    try {
      return this.productApiService.productAssetGet(productList, productAssetType).pipe(
        switchMap(
          async (result) => {
            if (productAssetType === EProductAssetType.Image || productAssetType === EProductAssetType.Thumbnail) {
              let productDefaultImage: string = await this.assetsService.productDefaultImageGetAsync();
              let productDefaultImageAssetItem = new ProductAssetItemDto({
                assetItemId: -1,
                content: productDefaultImage,
                contentType: 'image/jpg',
              });
              let productIdentifierResultMissingList: ProductIdentifierDto[] = [];

              productList.forEach(
                (productIdentifier => {
                  let match = result.productList?.find(
                    (resultEntry) => this.comparisonService.equalsProductIdentifierByNumberOrGuid(productIdentifier, resultEntry.productIdentifier)
                  );

                  if (match == undefined) {
                    productIdentifierResultMissingList.push(productIdentifier);
                    return;
                  }

                  if (match.assetList == undefined || match.assetList?.length <= 0) {
                    match.assetList = [productDefaultImageAssetItem];
                  }
                })
              );

              productIdentifierResultMissingList.forEach(
                (productIdentifier) => {
                  result.productList?.push(new ProductAssetDto({
                    productIdentifier: productIdentifier,
                    assetList: [productDefaultImageAssetItem]
                  }));
                }
              );
            }

            return result;
          }
        )
      );
    } catch (error) {
      this.logger.error(error, this.constructor.name);
      return of(productAssetGetQueryResult);
    }
  }

  public productAssetGetById(assetId: number): Observable<ProductAssetGetByIdQueryResult> {
    if (!this.allowDocumentDownload) {
      this.logger.error("Tried to download an asset in service though they aren't allowed.", this.constructor.name);
      throw new BasicError("Illegal access", ErrorCodeService.formatErrorCode(EError.F140230));
    }

    return this.productApiService.productAssetGetById(assetId);
  }

  public productQuantityScaleGet(productList?: ProductIdentifierDto[] | undefined): Observable<ProductQuantityScaleGetQueryResult> {
    return this.productApiService.productQuantityScaleGet(productList);
  }

  public productAssortmentGet(productList?: ProductIdentifierDto[] | undefined): Observable<ProductAssortmentGetQueryResult> {
    return this.productApiService.productAssortmentGet(productList);
  }

  public productOptionalGet(productList?: ProductIdentifierDto[] | undefined): Observable<ProductOptionalGetQueryResult> {
    return this.productApiService.productOptionalGet(productList);
  }

  public productGetByTechGroupFilter(techGroupFilter?: TechGroupFilterDto | undefined): Observable<ProductGetByTechGroupFilterQueryResult> {
    return this.productApiService.productGetByTechGroupFilter(techGroupFilter);
  }

  public productGetByProductCategory(includeSubCategoryProducs: boolean, loadTechGroupNumberList: boolean, categoryKey?: string | undefined): Observable<ProductGetByProductCategoryQueryResult> {
    return this.productApiService.productGetByProductCategory(includeSubCategoryProducs, loadTechGroupNumberList, categoryKey);
  }

  public productGetCategoryTree(productList?: ProductIdentifierDto[] | undefined): Observable<ProductGetCategoryTreeQueryResult> {
    return this.productApiService.productGetCategoryTree(productList);
  }

  public productPromotionGet(cartDate?: Date): Observable<ProductPromotionGetQueryResult> {
    return this.productApiService.productPromotionGet(cartDate);
  }

  public productPromotionDetailGet(productIdentifierList: ProductIdentifierDto[]): Observable<ProductPromotionDetailGetQueryResult> {
    return this.productApiService.productPromotionDetailGet(productIdentifierList, this.cartDeliveryDate);
  }

  public productGetByIntegraQuery(queryId: number, queryParameters?: { [key: string]: string; } | undefined): Observable<ProductGetByIntegraQueryQueryResult> {
    return this.productApiService.productGetByIntegraQuery(queryId, queryParameters);
  }

  public productPackagingTypeGet(productList?: ProductIdentifierDto[] | undefined): Observable<ProductPackagingTypeGetQueryResult> {
    return this.productApiService.productPackagingTypeGet(productList);
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const ProductServiceProvider: Provider[] = [
  { provide: IProductServiceToken, useExisting: ProductService }
];
